import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import type { Me } from '@ili/shared';
import { get, post } from './api';
import { setPrefs } from './prefs';

export function useMe() {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await get<{ me: Me | null }>('/me')).me,
    staleTime: 60_000,
    retry: 1,
  });
  return { me: q.data ?? null, loading: q.isPending, refetch: q.refetch };
}

export function useAuthActions() {
  const qc = useQueryClient();
  const afterAuth = (me: Me) => {
    qc.setQueryData(['me'], me);
    if (me.locale) setPrefs({ language: me.locale });
    qc.invalidateQueries();
  };
  const signIn = useMutation({
    mutationFn: (v: { email: string; password: string }) => post<{ me: Me }>('/auth/login', v),
    onSuccess: (r) => afterAuth(r.me),
  });
  const signOut = useMutation({
    mutationFn: () => post('/auth/logout'),
    onSuccess: async () => {
      await clearPersonalCaches();
      qc.setQueryData(['me'], null);
      qc.clear();
    },
  });
  return { signIn, signOut, afterAuth };
}

/** Redirects to sign-in (and back afterwards) when there is no session. */
export function RequireAuth({ children, staff, admin }: { children: ReactNode; staff?: boolean; admin?: boolean }) {
  const { me, loading } = useMe();
  const loc = useLocation();
  if (loading) return <div className="page"><div className="spinner" role="status" aria-label="Loading" /></div>;
  if (!me) return <Navigate to={`/signin?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (staff && me.accountType !== 'staff') return <Navigate to="/" replace />;
  if (admin && !me.isAdmin) return <Navigate to="/staff" replace />;
  return <>{children}</>;
}

/** Remove cached personal data from the service worker cache (sign out / account deletion on a shared phone). */
export async function clearPersonalCaches(): Promise<void> {
  try {
    await caches.delete('api');
  } catch {
    /* no Cache Storage */
  }
}
